import { useEffect, useRef, useState } from 'react';
import type { DisplayPublic } from '../api';
import { ChannelChrome } from './ChannelChrome';

interface AdServeResponse {
  creativeId: string;
  campaignId: string;
  advertiserName: string;
  type: 'IMAGE' | 'VIDEO' | 'HTML5' | 'SPONSORED_OVERLAY';
  mediaUrl: string;
  thumbnailUrl: string | null;
  durationSeconds: number;
  zoneTargets: string[];
}

const FALLBACK_INTERVAL_MS = 8_000;

function readDisplayToken(): string | null {
  try {
    const raw = localStorage.getItem('scorelync-display');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.displayToken ?? null;
  } catch {
    return null;
  }
}

export function AdReelChannel({
  display,
  connected,
  onUnpair,
}: {
  display: DisplayPublic;
  connected: boolean;
  onUnpair: () => void;
}) {
  const [ad, setAd] = useState<AdServeResponse | null>(null);
  const [empty, setEmpty] = useState(false);
  const impressionIdRef = useRef<string | null>(null);
  const startedAtRef = useRef<number>(0);
  const tokenRef = useRef<string | null>(readDisplayToken());

  // End the currently-running impression (if any).
  async function endCurrent() {
    const token = tokenRef.current;
    const id = impressionIdRef.current;
    if (!token || !id) return;
    const elapsed = (performance.now() - startedAtRef.current) / 1000;
    impressionIdRef.current = null;
    try {
      await fetch(`/api/ads/impressions/${encodeURIComponent(id)}/end`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ durationSeconds: elapsed }),
      });
    } catch {
      // best-effort: missed impression-end is fine for v1
    }
  }

  // Fetch the next creative and start an impression.
  async function fetchNext() {
    const token = tokenRef.current;
    if (!token) return;
    await endCurrent();
    try {
      const res = await fetch('/api/ads/next?zone=ad_reel', {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setEmpty(true);
        setAd(null);
        return;
      }
      const next = (await res.json()) as AdServeResponse | null;
      if (!next) {
        setEmpty(true);
        setAd(null);
        return;
      }
      setEmpty(false);
      setAd(next);

      // Start impression.
      const start = await fetch('/api/ads/impressions/start', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          creativeId: next.creativeId,
          zone: 'ad_reel',
        }),
      });
      if (start.ok) {
        const body = (await start.json()) as { id: string };
        impressionIdRef.current = body.id;
        startedAtRef.current = performance.now();
      }
    } catch {
      setEmpty(true);
      setAd(null);
    }
  }

  useEffect(() => {
    fetchNext();
    return () => {
      endCurrent();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotation timer: when the current ad finishes its duration, fetch
  // the next one. If we got back "no ads", retry after a fallback delay.
  useEffect(() => {
    const ms = ad
      ? Math.max(1, ad.durationSeconds) * 1000
      : FALLBACK_INTERVAL_MS;
    const id = setTimeout(fetchNext, ms);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad?.creativeId, empty]);

  return (
    <ChannelChrome display={display} connected={connected} onUnpair={onUnpair}>
      <div className="flex flex-1 items-center justify-center bg-black">
        {ad ? <AdSlide ad={ad} /> : <EmptyState empty={empty} />}
      </div>
      <div className="border-t border-white/5 px-8 py-3 text-center text-xs text-white/30">
        {ad ? (
          <>
            {ad.advertiserName} · {ad.durationSeconds}s
          </>
        ) : (
          <>Ad reel · waiting for campaigns</>
        )}
      </div>
    </ChannelChrome>
  );
}

function AdSlide({ ad }: { ad: AdServeResponse }) {
  if (ad.type === 'VIDEO') {
    return (
      <video
        src={ad.mediaUrl}
        className="h-full w-full object-cover"
        autoPlay
        muted
        playsInline
      />
    );
  }
  if (ad.type === 'HTML5') {
    return (
      <iframe
        src={ad.mediaUrl}
        title={ad.advertiserName}
        className="h-full w-full border-0"
      />
    );
  }
  // IMAGE or SPONSORED_OVERLAY: render as an image.
  return (
    <img
      src={ad.mediaUrl}
      alt={ad.advertiserName}
      className="h-full w-full object-cover"
    />
  );
}

function EmptyState({ empty }: { empty: boolean }) {
  return (
    <div className="px-12 text-center">
      <div className="text-2xl font-semibold text-white/70">
        {empty ? 'No active campaigns' : 'Loading ads…'}
      </div>
      <div className="mt-2 text-sm text-white/40">
        Ads will appear here as soon as campaigns are activated for this
        venue.
      </div>
    </div>
  );
}
